import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import Rating from '@mui/material/Rating';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';

import { BreadcrumbHeader, EmptyState, ActionMenu, ConfirmModal } from '../../components/shared';
import ImportInventoryModal from '../../components/shared/ImportInventoryModal';
import { suppliers as initialSuppliers, supplierQuotes, quoteHistory } from '../../services/dataClient';
import '../../styles/design-tokens.css';
import { fieldSx } from '../../theme/fieldStyles';

// ─── Compute rating from quote sources ────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";function computeSupplierRating(supplierName) {
  const terminal = [
  ...quoteHistory.filter((q) => q.supplierName === supplierName && (q.status === 'Accepted' || q.status === 'Declined')),
  ...supplierQuotes.filter((q) => q.supplierName === supplierName && (q.status === 'Accepted' || q.status === 'Declined'))];

  if (terminal.length === 0) return null;
  const accepted = terminal.filter((q) => q.status === 'Accepted').length;
  const raw = 1 + accepted / terminal.length * 4;
  return Math.round(raw * 2) / 2;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  name: '',
  contactName: '',
  contactEmail: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  avgLeadTimeDays: '',
  notes: '',
  active: true
};

// Table cell style constants (matching ReverseLogisticsPage pattern)
const TH = {
  backgroundColor: '#eef2f6',
  fontWeight: 600,
  fontSize: '0.75rem',
  color: 'var(--grey-600)',
  textTransform: 'none',
  letterSpacing: 'normal',
  px: 2,
  py: '10px',
  borderBottom: '1px solid var(--grey-200)',
  whiteSpace: 'nowrap'
};

const TD = {
  px: 2,
  py: 0,
  border: 'none',
  fontSize: '0.875rem',
  color: 'var(--grey-800)',
  height: 48
};

// ─── Lead time chip ───────────────────────────────────────────────────────────
function LeadTimeChip({ days }) {
  let bg, color;
  if (days < 5) {bg = '#E8F5E9';color = '#2E7D32';} else
  if (days <= 10) {bg = '#FFF8E1';color = '#E65100';} else
  {bg = '#FFEBEE';color = '#B71C1C';}

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', px: '8px', py: '2px', borderRadius: '10px', backgroundColor: bg }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color, lineHeight: 1 }}>
        {days} days
      </Typography>
    </Box>);

}

// ─── Add / Edit modal ─────────────────────────────────────────────────────────
function SupplierModal({ open, mode, initial, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_FORM);

  const handleEnter = () =>
  setForm(
    mode === 'edit' && initial ?
    {
      name: initial.name,
      contactName: initial.contactName,
      contactEmail: initial.contactEmail,
      phone: initial.phone ?? '',
      address: initial.address ?? '',
      city: initial.city ?? '',
      state: initial.state ?? '',
      zip: initial.zip ?? '',
      avgLeadTimeDays: String(initial.avgLeadTimeDays),
      notes: initial.notes ?? '',
      active: initial.active
    } :
    EMPTY_FORM
  );

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const isValid =
  form.name && form.contactName && form.contactEmail &&
  form.address && form.city && form.state && form.zip &&
  form.avgLeadTimeDays && !isNaN(Number(form.avgLeadTimeDays));

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {if (reason !== 'backdropClick') onClose();}}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEnter: handleEnter }}>
      
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {mode === 'edit' ? 'Edit Supplier' : 'Add Supplier'}
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        <TextField
          size="small" label="Supplier Name" value={form.name} onChange={set('name')}
          fullWidth required sx={fieldSx} />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small" label="Contact Name" value={form.contactName} onChange={set('contactName')}
            fullWidth required sx={fieldSx} />
          
          <TextField
            size="small" label="Contact Email" type="email" value={form.contactEmail} onChange={set('contactEmail')}
            fullWidth required sx={fieldSx} />
          
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small" label="Phone (optional)" value={form.phone} onChange={set('phone')}
            fullWidth sx={fieldSx} />
          
          <TextField
            size="small" label="Avg Lead Time (days)" value={form.avgLeadTimeDays} onChange={set('avgLeadTimeDays')}
            sx={{ width: 180, ...fieldSx }} required
            inputProps={{ inputMode: 'numeric' }} />
          
        </Box>

        <TextField
          size="small" label="Address" value={form.address} onChange={set('address')}
          fullWidth required sx={fieldSx} />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small" label="City" value={form.city} onChange={set('city')}
            fullWidth required sx={fieldSx} />
          
          <TextField
            size="small" label="State" value={form.state} onChange={set('state')}
            sx={{ width: 100, ...fieldSx }} required />
          
          <TextField
            size="small" label="Zip Code" value={form.zip} onChange={set('zip')}
            sx={{ width: 120, ...fieldSx }} required />
          
        </Box>

        <TextField
          size="small" label="Notes (optional)" value={form.notes} onChange={set('notes')}
          multiline rows={2} fullWidth sx={fieldSx} />

        {/* Status toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>Status</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={form.active ? 'active' : 'inactive'}
            onChange={(_, val) => {if (val) setForm((prev) => ({ ...prev, active: val === 'active' }));}}
            sx={{ '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontSize: '0.8125rem',
                fontWeight: 600,
                px: 2,
                py: '4px',
                borderColor: 'var(--grey-200)',
                color: 'var(--grey-500)',
                '&.Mui-selected': { backgroundColor: '#009688', color: '#fff', borderColor: '#009688' },
                '&.Mui-selected:hover': { backgroundColor: '#00796B' }
              }
            }}>
            
            <ToggleButton value="active">Active</ToggleButton>
            <ToggleButton value="inactive">Inactive</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          
          Cancel
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={!isValid}
          onClick={() => onSave({ ...form, avgLeadTimeDays: Number(form.avgLeadTimeDays) })}
          sx={{ backgroundColor: '#009688',
            '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: 'none',
            px: 2
          }}>
          
          {mode === 'edit' ? 'Save Changes' : 'Add Supplier'}
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function SuppliersPage() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState(initialSuppliers);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editTarget, setEditTarget] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deactTarget, setDeactTarget] = useState(null);

  const [importTarget, setImportTarget] = useState(null);
  const [banner, setBanner] = useState('');

  const showBanner = (msg) => {setBanner(msg);setTimeout(() => setBanner(''), 4000);};

  // Pre-compute rating for each supplier from quote sources
  const ratingMap = useMemo(() =>
  Object.fromEntries(suppliers.map((s) => [s.id, computeSupplierRating(s.name)])),
  [suppliers]);

  // Pre-compute total quote count (active + history) per supplier
  const quoteCountMap = useMemo(() =>
  Object.fromEntries(suppliers.map((s) => [
  s.id,
  supplierQuotes.filter((q) => q.supplierName === s.name).length +
  quoteHistory.filter((q) => q.supplierName === s.name).length]
  )),
  [suppliers]);

  const handleSort = (field) =>
  setSortConfig((prev) => ({
    field,
    direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
  }));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...suppliers]
    .filter((s) => showInactive ? !s.active : s.active)
    .filter((s) =>
    !q ||
    s.name.toLowerCase().includes(q) ||
    s.contactName.toLowerCase().includes(q) ||
    s.contactEmail.toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const av = a[sortConfig.field] ?? '';
      const bv = b[sortConfig.field] ?? '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [suppliers, showInactive, search, sortConfig]);

  const handleSave = (form) => {
    if (modalMode === 'edit') {
      setSuppliers((prev) =>
      prev.map((s) => s.id === editTarget.id ? { ...s, ...form } : s)
      );
      showBanner('Supplier updated successfully.');
    } else {
      const code = `SUP-${String(suppliers.length + 1).padStart(2, '0')}`;
      setSuppliers((prev) => [
      ...prev,
      { id: `sup-new-${Date.now()}`, code, ...form }]
      );
      showBanner('Supplier added successfully.');
    }
    setModalOpen(false);
  };

  const handleDeactivate = () => {
    setSuppliers((prev) =>
    prev.map((s) => s.id === deactTarget.id ? { ...s, active: false } : s)
    );
    setConfirmOpen(false);
    setDeactTarget(null);
  };

  const handleActivate = (supplier) => {
    setSuppliers((prev) =>
    prev.map((s) => s.id === supplier.id ? { ...s, active: true } : s)
    );
    showBanner(`${supplier.name} activated successfully.`);
  };

  // Sort label helper
  const sortDir = (field) => sortConfig.field === field ? sortConfig.direction : false;

  const COLUMNS = [
  { field: 'name', label: 'Name', sortable: true, width: 'auto' },
  { field: 'contactName', label: 'Contact Name', sortable: true, width: 'auto' },
  { field: 'contactEmail', label: 'Contact Email', sortable: false, width: 'auto' },
  { field: 'phone', label: 'Phone', sortable: false, width: 160 },
  { field: '_location', label: 'Location', sortable: false, width: 160 },
  { field: '_quotes', label: 'Quotes', sortable: false, width: 80 },
  { field: 'avgLeadTimeDays', label: 'Avg Lead Time', sortable: true, width: 140 },
  { field: 'rating', label: 'Rating', sortable: false, width: 160 },
  { field: '_actions', label: '', sortable: false, width: 56 }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Success banner */}
      {banner &&
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7',
          borderRadius: '8px', px: 2, py: '12px'
        }}>
        
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            {banner}
          </Typography>
        </Box>
      }

      <BreadcrumbHeader
        title="Suppliers"
        crumbs={[{ label: 'Inventory' }, { label: 'Suppliers' }]} />

      <ContentCard sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search by Name, Contact, or Email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment:
              <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} />
                </InputAdornment>

            }}
            sx={{ minWidth: 400, maxWidth: 600, backgroundColor: 'var(--paper)', borderRadius: 1, ...fieldSx }} />

          <Box sx={{ flex: 1 }} />

          {/* Show Inactive toggle */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
            onClick={() => setShowInactive((prev) => !prev)}>
            
            <Switch
              checked={showInactive}
              size="small"
              onChange={(e) => setShowInactive(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#009688' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#009688' }
              }} />
            
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-600)', userSelect: 'none' }}>
              Show Inactive
            </Typography>
          </Box>

          {/* Add Supplier button */}
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {setModalMode('create');setEditTarget(null);setModalOpen(true);}}
            sx={{ backgroundColor: '#009688',
              '&:hover': { backgroundColor: '#00796B' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: 'none'
            }}>
            
            Add Supplier
          </Button>
        </Box>

        <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
        {filtered.length === 0 ?
          <EmptyState title="No suppliers found" description="Try adjusting your filter or search." /> :

          <Table>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) =>
                <TableCell
                  key={col.field}
                  sx={{ ...TH, width: col.width !== 'auto' ? col.width : undefined }}>
                  
                    {col.sortable ?
                  <TableSortLabel
                    active={sortConfig.field === col.field}
                    direction={sortDir(col.field) || 'asc'}
                    onClick={() => handleSort(col.field)}
                    sx={{ color: 'var(--grey-600) !important',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      letterSpacing: 'normal',
                      '& .MuiTableSortLabel-icon': { opacity: sortConfig.field === col.field ? 1 : 0.3, fontSize: 14 }
                    }}>
                    
                        {col.label}
                      </TableSortLabel> :

                  col.label
                  }
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((supplier) =>
              <TableRow
                key={supplier.id}
                sx={{ height: 48,
                  '&:not(:last-child) td': { borderBottom: '1px solid var(--grey-200)' },
                  '&:hover td': { backgroundColor: 'var(--grey-50, #f8fafc)' },
                  '&:hover .action-cell': { visibility: 'visible' }
                }}>
                
                    {/* Name */}
                    <TableCell sx={TD}>
                      <Typography
                    onClick={() => navigate(`/inventory/suppliers/${supplier.id}`)}
                    sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                    
                        {supplier.name}
                      </Typography>
                    </TableCell>

                    {/* Contact Name */}
                    <TableCell sx={TD}>{supplier.contactName}</TableCell>

                    {/* Contact Email */}
                    <TableCell sx={TD}>
                      <Typography
                    component="a"
                    href={`mailto:${supplier.contactEmail}`}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ fontSize: '0.875rem', color: '#009688', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                    
                        {supplier.contactEmail}
                      </Typography>
                    </TableCell>

                    {/* Phone */}
                    <TableCell sx={TD}>{supplier.phone || '—'}</TableCell>

                    {/* Location */}
                    <TableCell sx={TD}>
                      {supplier.city && supplier.state ?
                  `${supplier.city}, ${supplier.state}` :
                  '—'}
                    </TableCell>

                    {/* Quotes */}
                    <TableCell sx={TD}>
                      <Typography sx={{ fontSize: '0.875rem', color: quoteCountMap[supplier.id] > 0 ? 'var(--grey-900)' : 'var(--grey-400)', fontWeight: quoteCountMap[supplier.id] > 0 ? 600 : 400 }}>
                        {quoteCountMap[supplier.id] > 0 ? quoteCountMap[supplier.id] : '—'}
                      </Typography>
                    </TableCell>

                    {/* Avg Lead Time */}
                    <TableCell sx={TD}>
                      <LeadTimeChip days={supplier.avgLeadTimeDays} />
                    </TableCell>

                    {/* Rating */}
                    <TableCell sx={TD}>
                      {ratingMap[supplier.id] !== null ?
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Rating
                      value={ratingMap[supplier.id]}
                      precision={0.5}
                      readOnly
                      size="small"
                      sx={{ '& .MuiRating-iconFilled': { color: '#009688' } }} />
                    
                          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)' }}>
                            {ratingMap[supplier.id].toFixed(1)}
                          </Typography>
                        </Box> :

                  <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>
                  }
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={{ ...TD, visibility: 'visible' }} className="action-cell">
                      <Box onClick={(e) => e.stopPropagation()}>
                        <ActionMenu
                      hideUntilRowHover
                      actions={[
                      {
                        label: 'View Details',
                        onClick: () => navigate(`/inventory/suppliers/${supplier.id}`)
                      },
                      {
                        label: 'Edit',
                        onClick: () => {setEditTarget(supplier);setModalMode('edit');setModalOpen(true);}
                      },
                      {
                        label: 'Import Inventory',
                        onClick: () => setImportTarget(supplier)
                      },
                      ...(supplier.active ?
                      [{
                        label: 'Deactivate',
                        onClick: () => {setDeactTarget(supplier);setConfirmOpen(true);}
                      }] :
                      [{
                        label: 'Activate',
                        onClick: () => handleActivate(supplier)
                      }])]
                      } />
                    
                      </Box>
                    </TableCell>
                  </TableRow>
              )}
            </TableBody>
          </Table>
          }
        </Box>
      </ContentCard>

      <SupplierModal
        open={modalOpen}
        mode={modalMode}
        initial={editTarget}
        onClose={() => setModalOpen(false)}
        onSave={handleSave} />

      <ImportInventoryModal
        open={Boolean(importTarget)}
        supplier={importTarget}
        onClose={() => setImportTarget(null)}
        onImported={(supplierName, count) => {
          setImportTarget(null);
          showBanner(`Imported ${count} new part${count !== 1 ? 's' : ''} for ${supplierName}.`);
        }} />

      <ConfirmModal
        open={confirmOpen}
        title="Deactivate Supplier"
        message={deactTarget ? `Are you sure you want to deactivate ${deactTarget.name}?` : ''}
        confirmLabel="Deactivate"
        onConfirm={handleDeactivate}
        onCancel={() => {setConfirmOpen(false);setDeactTarget(null);}} />
      
    </Box>);

}